import { useMemo } from 'react';

import Config from '@config';
import { normalizeInfiniteQueryData } from '@gasbi/shared/common/features/utils';
import { createApi } from '@reduxjs/toolkit/query/react';
import { useSelector } from 'react-redux';

import {
  AuditEntity,
  AuditEntityDetails,
  AuditEntityTypeInfo,
} from '@/features/audit/types';
import { CACHE_EXPIRATION_TIME } from '@/features/constants';
import { FiltersGrid, Pagination } from '@/features/types';
import { infiniteQueryOptions } from '@/features/utils';
import axiosBaseQuery from '@/utils/axiosBaseQuery';

export const auditApi = createApi({
  reducerPath: 'auditApi',
  baseQuery: axiosBaseQuery({
    baseUrl: `${Config.VITE_AUDIT_API_URL}`,
  }),
  keepUnusedDataFor: CACHE_EXPIRATION_TIME,
  tagTypes: ['Info', 'Entities', 'Entity'],
  endpoints: builder => ({
    getAuditInfo: builder.query<AuditEntityTypeInfo, string>({
      query: entityTypeName => ({
        url: `/entities/info`,
        params: {
          entityTypeName,
        },
      }),
      serializeQueryArgs: ({ endpointName }) => endpointName,
      providesTags: () => ['Info'],
    }),
    getAuditEntries: builder.infiniteQuery<
      AuditEntity,
      FiltersGrid & { entityId: string; anonymized?: boolean },
      Pagination
    >({
      infiniteQueryOptions,
      query: ({ queryArg: { anonymized = false, ...arg }, pageParam }) => ({
        url: `/entries/query${anonymized ? '/anonymized' : ''}`,
        params: { ...arg, ...pageParam },
      }),
      providesTags: () => ['Entities'],
    }),
    getAuditEntryDetails: builder.query<
      AuditEntityDetails,
      { auditEntryId: string; anonymized?: boolean }
    >({
      query: ({ auditEntryId, anonymized = false }) => ({
        url: `/entries/details/${auditEntryId}${anonymized ? '/anonymized' : ''}`,
      }),
      providesTags: result => [{ type: 'Entity', id: result?.auditEntryId }],
    }),
  }),
});

export const { useGetAuditInfoQuery, useGetAuditEntryDetailsQuery } = auditApi;

export const useGetAuditEntriesInfiniteQuery = normalizeInfiniteQueryData(
  auditApi.useGetAuditEntriesInfiniteQuery,
);

export const useAuditEntityTypeNameTranslationSelector = () => {
  const { data: { availableEntities = [] } = {} } = useSelector(
    auditApi.endpoints.getAuditInfo.select(undefined),
  );

  return useMemo(
    () =>
      availableEntities.reduce(
        (acc, entity) => ({
          ...acc,
          [entity.entityTypeName]: entity.entityDisplayName,
        }),
        {} as Record<string, string>,
      ),
    [availableEntities],
  );
};
